import { useState } from 'react';
import { useTranslations } from 'use-intl';
import { useQuery } from '@tanstack/react-query';
import { PageHeader, PageHeaderSkeleton, Stack, useDelayedLoading } from '@leanin/ui';
import { groupKeys } from '../../../lib/queries/groups';
import {
  useApproveMembershipRequest,
  useDeclineMembershipRequest,
} from '../../../lib/mutations/groups';
import { useMessageMember } from '../../groups/use-message-member';
import { InlineStatus } from '../../InlineStatus';
import { AdminJoinRequestsCard } from './AdminJoinRequestsCard';
import { AdminJoinRequestsCardSkeleton } from './AdminJoinRequestsCard.skeleton';

// Pending join requests, for circles and networks. (Spaces don't have
// request-to-join.) Each kind's queries differ in URL but share a shape;
// dispatched on `kind`.

type Props = {
  readonly kind: 'circle' | 'network';
  readonly groupId: string;
};

export function GroupRequestsSection({ kind, groupId }: Props) {
  const t = useTranslations('Group');
  // Parallel queries, each gated by `kind` via `enabled`. The inactive one
  // is a no-op; the active one populates `query`. This shape keeps TS
  // overload inference clean (no conditional config arg into useQuery).
  const circleQuery = useQuery({
    ...groupKeys.circle.joinRequests(groupId),
    enabled: kind === 'circle',
  });
  const networkQuery = useQuery({
    ...groupKeys.network.joinRequests(groupId),
    enabled: kind === 'network',
  });
  const query = kind === 'circle' ? circleQuery : networkQuery;

  const approve = useApproveMembershipRequest();
  const decline = useDeclineMembershipRequest();
  const { messageMember, error: messageError } = useMessageMember();
  const [decisionError, setDecisionError] = useState<string | null>(null);
  const isPending = approve.isPending || decline.isPending;

  const handleApprove = (requestId: string) => {
    setDecisionError(null);
    approve.mutate(
      { groupId, requestId, kind },
      {
        onError: (err) =>
          setDecisionError(
            err instanceof Error
              ? err.message
              : t('admin.requests.approveError'),
          ),
      },
    );
  };
  const handleDecline = (requestId: string) => {
    setDecisionError(null);
    decline.mutate(
      { groupId, requestId, kind },
      {
        onError: (err) =>
          setDecisionError(
            err instanceof Error
              ? err.message
              : t('admin.requests.declineError'),
          ),
      },
    );
  };

  const showSkeleton = useDelayedLoading(query.data === undefined);
  if (showSkeleton) {
    return (
      <Stack gap="lg">
        <PageHeaderSkeleton />
        <AdminJoinRequestsCardSkeleton rows={3} />
      </Stack>
    );
  }
  if (query.data === undefined) return null;

  // The fetch boundary (fetchGroupJoinRequests) already scopes to pending —
  // trust it rather than re-filtering here.
  const requests = query.data.items;

  return (
    <Stack gap="lg">
      <PageHeader
        title={t('admin.requests.title')}
        subtitle={t('admin.requests.subtitle')}
      />
      {messageError !== null ? (
        <InlineStatus>{messageError}</InlineStatus>
      ) : null}
      {decisionError !== null ? (
        <InlineStatus>{decisionError}</InlineStatus>
      ) : null}
      <AdminJoinRequestsCard
        requests={requests}
        onApprove={handleApprove}
        onDecline={handleDecline}
        onMessage={messageMember}
        isPending={isPending}
      />
    </Stack>
  );
}
